import React from "react";
import { Card, CardHeader, CardContent, Box, Typography, Divider } from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "Unknown date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function PaymentList({
  title = "Displayed Payments",
  entries = [],
  currency = "USD",
  maxInvoices = 6,
  mode = "cash",
}) {
  const history = useHistory();
  const items = entries.slice(0, maxInvoices);
  const isCash = mode === "cash";
  const totalPayments = isCash ? entries.reduce((sum, invoice) => sum + (invoice.payments?.length || 0), 0) : 0;
  const totalAccrualAmount = !isCash ? entries.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0) : 0;
  const subheader = entries.length
    ? isCash
      ? `${entries.length} invoice${entries.length === 1 ? "" : "s"} • ${totalPayments} payment${totalPayments === 1 ? "" : "s"}`
      : `${entries.length} invoice${entries.length === 1 ? "" : "s"} • ${currencyFmt(totalAccrualAmount, currency)}`
    : undefined;

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader title={title} subheader={subheader} />
      <CardContent style={{ maxHeight: 300, overflowY: "auto" }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No activity in this range.
          </Typography>
        ) : (
          items.map((invoice, idx) => (
            <Box
              key={invoice.invoiceId || idx}
              mb={idx === items.length - 1 ? 0 : 2}
              onClick={() => invoice.invoiceId && history.push(`/invoice/${invoice.invoiceId}`)}
              style={{ cursor: invoice.invoiceId ? "pointer" : "default" }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {invoice.clientStore || invoice.clientName || (invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : "Invoice")}
                </Typography>
                {invoice.invoiceId && (
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <Typography variant="caption" color="primary">
                      View invoice
                    </Typography>
                    <LaunchIcon fontSize="small" color="primary" />
                  </Box>
                )}
              </Box>
              {isCash ? (
                invoice.payments?.slice(0, 5).map((pay, payIdx) => (
                  <Box
                    key={`${invoice.invoiceId || idx}-pay-${payIdx}`}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={0.5}
                  >
                    <Box>
                      <Typography variant="body2">{currencyFmt(pay.amount, currency)}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(pay.date)}{pay.paymentMethod ? ` • ${pay.paymentMethod}` : ""}
                      </Typography>
                    </Box>
                    {pay.note && (
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8, textAlign: "right" }}>
                        {pay.note}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {currencyFmt(invoice.amount, currency)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(invoice.accrualDate)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {idx !== items.length - 1 && <Divider style={{ marginTop: 8 }} />}
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
